import { ArrowUpRight, CircleCheck, CircleDashed, IndianRupee } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ProgressBar } from '@/components/ui/ProgressBar';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { PlatformStats } from '@/types/institute';

interface SubscriptionPanelProps {
  stats: PlatformStats | null;
  isLoading: boolean;
}

export function SubscriptionPanel({ stats, isLoading }: SubscriptionPanelProps) {
  const subscription = stats?.subscription;

  // Agreed fees of institutes flagged PAID — not money received, which the
  // income strip takes from the payment ledger. Kept as agreed fees so the
  // share below compares like with like against `billed`.
  const billed = Number(subscription?.billed ?? 0);
  const paidAmount = Number(subscription?.paidAmount ?? 0);
  const paidShare = billed > 0 ? (paidAmount / billed) * 100 : 0;

  const unpaidInstitutes = subscription?.unpaidInstitutes ?? [];
  const unpaidCount = subscription?.unpaidCount ?? 0;
  // Who actually owes the unpaid amount: unpaid customers with no fee owe
  // nothing, and are explained separately below.
  const owingCount = subscription?.unpaidWithFeeCount ?? 0;
  const noFeeCount = subscription?.noFeeCount ?? 0;

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-bold text-slate-900 dark:text-white">Yearly licence fees</h2>
          <p className="mt-0.5 text-[12px] text-slate-500 dark:text-slate-400 font-medium">
            {formatCurrency(subscription?.billed)} agreed across every institute with a fee set.
          </p>
        </div>
        <span
          className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-indigo-50 text-[#5b52f9] dark:bg-indigo-950/50 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/60 shadow-2xs"
          aria-hidden
        >
          <IndianRupee className="h-4 w-4" />
        </span>
      </div>

      {isLoading ? (
        <SkeletonLines count={4} />
      ) : (
        <>
          {/* Unified Financial Split Summary */}
          <div className="rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-gradient-to-br from-slate-50/90 via-white to-slate-50/60 dark:from-slate-800/40 dark:via-slate-900 dark:to-slate-800/20 p-4 sm:p-5 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
            <div className="grid grid-cols-1 divide-y sm:grid-cols-2 sm:divide-y-0 sm:divide-x divide-slate-200/80 dark:divide-slate-800 gap-4 sm:gap-0">
              {/* Left Column: Paid */}
              <div className="sm:pr-5">
                <div className="flex items-center gap-1.5">
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/80 dark:text-emerald-400">
                    <CircleCheck className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                    Marked paid
                  </span>
                </div>
                <p className="mt-1.5 text-[28px] font-black text-slate-900 dark:text-white tabular-nums tracking-tight leading-none">
                  {formatCurrency(subscription?.paidAmount)}
                </p>
                <p className="mt-1.5 text-[11.5px] text-slate-500 dark:text-slate-400 font-medium">
                  agreed fees of{' '}
                  <span className="font-bold text-slate-800 dark:text-slate-200 tabular-nums">
                    {subscription?.paidCount ?? 0}
                  </span>{' '}
                  {(subscription?.paidCount ?? 0) === 1 ? 'institute' : 'institutes'} marked paid
                </p>
              </div>

              {/* Right Column: Unpaid */}
              <div className="pt-4 sm:pt-0 sm:pl-5">
                <div className="flex items-center gap-1.5">
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/80 dark:text-amber-400">
                    <CircleDashed className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                    Unpaid
                  </span>
                </div>
                <p className="mt-1.5 text-[28px] font-black text-slate-900 dark:text-white tabular-nums tracking-tight leading-none">
                  {formatCurrency(subscription?.unpaidAmount)}
                </p>
                <p className="mt-1.5 text-[11.5px] text-slate-500 dark:text-slate-400 font-medium">
                  still owed by{' '}
                  <span className="font-bold text-slate-800 dark:text-slate-200 tabular-nums">
                    {owingCount}
                  </span>{' '}
                  {owingCount === 1 ? 'institute' : 'institutes'}
                </p>
              </div>
            </div>

            {billed > 0 && (
              <div className="mt-4 pt-3.5 border-t border-slate-100 dark:border-slate-800">
                <ProgressBar
                  value={paidShare}
                  label="Agreed fees marked paid"
                  caption={`${Math.round(paidShare)}% of agreed fees`}
                />
              </div>
            )}
          </div>

          {noFeeCount > 0 && (
            <p className="text-[12px] text-slate-500 dark:text-slate-400 font-medium">
              {noFeeCount === 1
                ? '1 institute has no fee agreed yet, so it is counted as unpaid but owes nothing.'
                : `${noFeeCount} institutes have no fee agreed yet, so they are counted as unpaid but owe nothing.`}
            </p>
          )}

          {/* ---------------- waiting on payment ---------------- */}
          <div className="flex flex-col gap-2 border-t border-slate-100 dark:border-slate-800 pt-3">
            <h3 className="text-[14px] font-bold  tracking-wide text-slate-600 dark:text-slate-400">
              Waiting on payment
            </h3>

            {unpaidInstitutes.length === 0 ? (
              <p className="py-2 text-[13px] text-slate-500 dark:text-slate-400">
                Every institute with a fee agreed has paid it.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-slate-100 dark:divide-slate-800">
                {unpaidInstitutes.map((institute) => (
                  <li key={institute.uuid}>
                    <Link
                      to={`/platform/institutes/${institute.uuid}`}
                      className="group flex items-center justify-between gap-3 py-2 px-2 rounded-xl transition-colors bg-slate-50  hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-[13.5px] font-bold text-slate-800 dark:text-slate-100 group-hover:text-[#5b52f9]">
                          {institute.displayName}
                        </span>
                        <span className="block truncate text-[11.5px] text-slate-400 dark:text-slate-500 font-medium">
                          {institute.city}
                        </span>
                      </span>

                      <span className="flex shrink-0 items-center gap-2">
                        <span
                          className={cn(
                            'text-[13px] font-bold tabular-nums',
                            institute.subscriptionAmount === null
                              ? 'text-slate-400 dark:text-slate-500 font-semibold'
                              : 'text-amber-600 dark:text-amber-400',
                          )}
                        >
                          {institute.subscriptionAmount === null
                            ? 'No fee set'
                            : formatCurrency(institute.subscriptionAmount)}
                        </span>
                        <ArrowUpRight
                          className="h-3.5 w-3.5 text-slate-400 group-hover:text-[#5b52f9] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          aria-hidden
                        />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {unpaidCount > unpaidInstitutes.length && (
              <p className="px-2 text-[11.5px] text-slate-400 dark:text-slate-500 font-medium">
                and {unpaidCount - unpaidInstitutes.length} more
              </p>
            )}

            <Link
              to="/platform/institutes"
              className="inline-flex items-center gap-1 self-start px-2 text-[12.5px] font-bold text-[#5b52f9] transition-colors hover:underline"
            >
              See every institute
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </div>
        </>
      )}
    </section>
  );
}
